import type { TSiteAddress } from "@/domains/site/site-addresses.types";
import type { SubscriberModel } from "@/domains/subscriber/subscriber.types";
import type { AnemicReference } from "@/types/backoffice/dto/common/references";
import type { TSimpleCompanyDto } from "@/types/backoffice/dto/company/simple-company-dto";
import type { THeatPumpDeclarationDto } from "@/types/backoffice/dto/heatpump/heatpump-declaration-dto";
import type { TMissionDto } from "@/types/backoffice/dto/mission/mission-dto";
import type { TRefMissionDto } from "@/types/backoffice/dto/mission/ref-mission-dto";
import type { TOperationDto } from "@/types/backoffice/dto/operation/operation-dto";
import type { ESiteDeactivatedState, ESubscriberOffer } from "@/types/backoffice/dto/site/site-dto";
import type { TSitePartnerDto } from "@/types/backoffice/dto/site/site-partner-dto";
import type { TSitePdlDto } from "@/types/backoffice/dto/site/site-pdl-dto";
import type { TSiteTypeLayoutDto } from "@/types/backoffice/dto/site/site-type-layout-dto";
import type { CompanyId, ExternalSiteId, OwnerStatusId, SiteId, SubscribedPowerId } from "@/types/common/ids";

import { SiteBusinessSegment } from "@/types/backoffice/dto/site/site-dto";

// Phase 1 : Re-use siteDtoSchema (site-dto.ts) structure as much as possible.
// we need to make the migration as seamless as possible.
// Only in a second phase we will allow ourselves to deviate from the former sitev1-DTO-based structure.
export interface SiteModel
  // Site is such a big model that we split it ourselves in multiple sub-models for readability / maintainability
  // but ideally the backend DTO would be structured that way from the start.
  extends SiteProperties, SiteSubscriber, SiteDSO, SiteAppliances, SitePhoning, SitePartnership {
  id: SiteId;
  externId: ExternalSiteId | null;
  siteAddress: TSiteAddress;
}

export enum EApplianceEstimateCode {
  HEATER = "HEATER",
  WATER_HEATER = "WATER_HEATER",
  HEAT_PUMP = "HEAT_PUMP",
}

export interface SiteApplianceEstimate {
  code: EApplianceEstimateCode | string; // we have only 3 code that we care in voltacall, but we want to keep all of them
  quantity: number;
}

export interface SitePartnership {
  mission: TMissionDto | null;
  operation: TOperationDto | null;
  partner: TSitePartnerDto | null;
}

export interface SitePhoning {
  refMission: TRefMissionDto | null;
}

export interface SiteAppliances {
  eligibleApplianceNumber: number | null;
  applianceEstimates: SiteApplianceEstimate[];
  heatPumpDeclarations: THeatPumpDeclarationDto[];
}

export interface SiteDSO {
  grd: TSimpleCompanyDto | null; // TODO : Use CommonCompany type
  deliveryPoint: string | null; // TODO : Use DeliveryPoint type
  pdl: TSitePdlDto | null; // TODO : Use DeliveryPoint type
}

export interface SiteProperties {
  businessSegment: SiteBusinessSegment;
  surface: number | null;
  heatingSystemMoreThan2Years: boolean | null;
  buildingMoreThan2Years: boolean | null;
  showPartnerBadge: boolean;
  siteTypeLayout: TSiteTypeLayoutDto | null;
  deactivatedState: ESiteDeactivatedState | null;
  wasEquippedWithTiko: boolean | null;
}

export interface SiteSubscriber {
  subscriber: SubscriberModel | null;
  subscriberCompany: ISiteCompany | null;
  subOwnerStatusType: AnemicReference<OwnerStatusId> | null;
  subscribedPowerType: {
    id: SubscribedPowerId;
    description: string;
    forLargeConsumptionSite: boolean;
    power: number;
    refCountryId: number;
  };
  mandatoryInstallation: boolean;
  subscriberOffer: ESubscriberOffer | null;
}

export interface ISiteCompany {
  id: CompanyId;
  name: string;
  corporateName: string | null;
  address: string | null;
  postCode: string | null;
  town: string | null;
}
